"use client";

import * as React from "react";
import Link from "next/link";
import {
  ArrowRight,
  BookOpen,
  Mail,
  Cpu,
  Award,
  Shield,
  Globe,
  Sparkles,
  Crown,
  Activity,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { GradientText } from "@/components/ui/gradient-text";
import { AnimatedStat } from "@/components/ui/animated-stat";
import { Badge } from "@/components/ui/badge";

const STATS = [
  { value: 15, suffix: "+", label: "Years", icon: Activity, cls: "text-red", bg: "from-red/15" },
  { value: 18, suffix: "", label: "Countries", icon: Globe, cls: "text-cyan", bg: "from-cyan/15" },
  { value: 60, suffix: "+", label: "Services", icon: Cpu, cls: "text-purple", bg: "from-purple/15" },
  { value: 83.3, suffix: "%", label: "Success", icon: Award, cls: "text-emerald", bg: "from-emerald/15" },
  { value: 0, suffix: "", label: "Breaches · 15+ Yrs", icon: Shield, cls: "text-amber-gold", bg: "from-amber-gold/15" },
  { value: 70, suffix: "%", label: "Women", icon: Crown, cls: "text-crimson", bg: "from-crimson/15" },
];

const CREDENTIALS = [
  "MBBS · Gynaecology · Delhi",
  "Nobel Peace Prize · WEF",
  "ISO 27001 · SOC 2 Type II",
  "FIPS 140-3 L3",
];

export function HeroSection() {
  return (
    <section
      id="hero"
      aria-label="Hero"
      className="relative flex items-center overflow-hidden pt-28 pb-16 lg:pt-32 lg:pb-20"
    >
      {/* Layered background */}
      <div aria-hidden className="absolute inset-0 bg-gradient-hero" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:48px_48px] pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-red/12 blur-3xl animate-fade-in"
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -right-24 w-[640px] h-[640px] rounded-full bg-purple/10 blur-3xl animate-fade-in animation-delay-300"
      />
      <div
        aria-hidden
        className="absolute top-1/3 right-1/4 w-[420px] h-[420px] rounded-full bg-cyan/8 blur-3xl animate-fade-in animation-delay-500"
      />
      <div
        aria-hidden
        className="absolute bottom-1/4 left-1/3 w-[340px] h-[340px] rounded-full bg-emerald/8 blur-3xl animate-fade-in animation-delay-700"
      />

      <div className="container-1720 relative z-elevated">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left: text */}
          {/* perf 2026-09-12 (W3 JS-diet): this subtree holds the LCP h1/paragraph.
              History: motion initial="hidden" (hydration-gated paint, +1.8s) ->
              initial={false} -> plain div (initial={false} meant no animation
              remained, only hydration weight). Fonts: swap + 88% size-adjust;
              fonts-gate retired (globals.css). LCP paints with first CSS paint. */}
          <div
            className="max-w-2xl"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-tinted-red border border-red/20 mb-6"
            >
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">The Operator</span>
              <span className="text-caption text-muted-foreground">·</span>
              <span className="text-caption text-muted-foreground">COO of CryptoMize & MaxiMize Infinium</span>
            </div>

            <h1
              className="text-display-lg mb-6 leading-[1.05] tracking-tight"
            >
              <span className="block text-foreground">A physician who</span>
              <GradientText variant="sovereign" as="span" className="block">
                became an operator.
              </GradientText>
            </h1>

            <p
              className="text-body-lg text-muted-foreground leading-relaxed mb-8 max-w-xl"
            >
              At the intersection of <span className="text-red font-semibold">medicine</span>,{" "}
              <span className="text-purple font-semibold">technology</span>, and{" "}
              <span className="text-cyan font-semibold">global strategy</span>. Overseeing sixty-plus integrated
              services across eighteen countries on three continents.
            </p>

            <div
              className="flex flex-wrap items-center gap-3 mb-8"
            >
              <Button asChild size="lg" className="h-12 px-6 bg-gradient-sovereign shadow-lg shadow-red/30">
                <Link href="/contact/">
                  Book Dr. Jyoti Kush
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-5 glass">
                <Link href="/insights/">
                  <BookOpen className="size-4" />
                  Read the Insights
                </Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="h-12 px-5 hover:bg-muted/60 text-foreground">
                <Link href="/the-operators-notebook/">
                  <Mail className="size-4" />
                  Subscribe
                </Link>
              </Button>
            </div>

            <div
              className="flex flex-wrap items-center gap-2"
            >
              {CREDENTIALS.map((c) => (
                <Badge key={c} variant="outline" className="text-caption font-semibold glass-tinted-amber-gold border-amber-gold/40 text-foreground">
                  <CheckCircle2 className="size-3 mr-1 text-amber-gold" />
                  {c}
                </Badge>
              ))}
            </div>
          </div>

          {/* Right: stat grid — CSS compositor entrance (was motion with 0.3-0.7s
              JS delays; CSS starts at parse time, no hydration dependency) */}
          <div
            className="stat-in grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4"
          >
            {STATS.map((s, i) => (
              <div
                key={s.label}
                style={{ animationDelay: `${(0.1 + i * 0.06).toFixed(2)}s` }}
                className={`stat-in glass-card rounded-2xl p-4 lg:p-5 bg-gradient-to-br ${s.bg} to-transparent relative overflow-hidden group`}
              >
                <div aria-hidden className="absolute -top-12 -right-12 size-32 rounded-full bg-current opacity-[0.06] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <s.icon className={`size-5 ${s.cls} mb-3`} />
                <div className={`text-display-sm font-bold ${s.cls} leading-none mb-1.5`}>
                  <AnimatedStat value={s.value} suffix={s.suffix} />
                </div>
                <div className="text-caption text-muted-foreground font-semibold tracking-wide uppercase">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
